import React, { useState } from 'react';
import { Plus, FileText, Calendar, DollarSign, ChevronDown, ChevronUp, Plane, Hotel, Car, Utensils, Receipt, MoreHorizontal, CheckCircle, Clock, XCircle, AlertCircle } from 'lucide-react';
export function ExpensesPage() {
  const [activeTab, setActiveTab] = useState('all');
  const [expandedReportId, setExpandedReportId] = useState<number | null>(null);
  const toggleReport = (id: number) => {
    if (expandedReportId === id) {
      setExpandedReportId(null);
    } else {
      setExpandedReportId(id);
    }
  };
  const summaryStats = [{
    label: 'Pending Reimbursement',
    value: '$2,450',
    color: 'bg-amber-50 text-amber-700',
    icon: Clock
  }, {
    label: 'Approved This Month',
    value: '$8,320',
    color: 'bg-emerald-50 text-emerald-700',
    icon: CheckCircle
  }, {
    label: 'Total Submitted YTD',
    value: '$42,800',
    color: 'bg-blue-50 text-blue-700',
    icon: FileText
  }];
  const expenses = [{
    id: 1,
    title: 'Q4 Strategy Meeting — San Francisco',
    dates: 'Nov 12 - Nov 15, 2023',
    amount: '$1,245.00',
    status: 'Submitted',
    items: 8,
    lineItems: [{
      id: 101,
      date: 'Nov 12',
      category: 'Flight',
      desc: 'UA 4521 JFK-SFO',
      amount: '$450.00',
      receipt: true,
      icon: Plane
    }, {
      id: 102,
      date: 'Nov 12',
      category: 'Transport',
      desc: 'Uber to Hotel',
      amount: '$45.00',
      receipt: true,
      icon: Car
    }, {
      id: 103,
      date: 'Nov 12',
      category: 'Meals',
      desc: 'Team Dinner',
      amount: '$120.00',
      receipt: true,
      icon: Utensils
    }, {
      id: 104,
      date: 'Nov 13',
      category: 'Hotel',
      desc: 'Marriott Union Square',
      amount: '$280.00',
      receipt: true,
      icon: Hotel
    }]
  }, {
    id: 2,
    title: 'Client Workshop — Chicago',
    dates: 'Oct 24 - Oct 26, 2023',
    amount: '$850.00',
    status: 'Approved',
    items: 5,
    lineItems: [{
      id: 201,
      date: 'Oct 24',
      category: 'Flight',
      desc: 'AA 1234 JFK-ORD',
      amount: '$320.00',
      receipt: true,
      icon: Plane
    }, {
      id: 202,
      date: 'Oct 24',
      category: 'Hotel',
      desc: 'Hyatt Regency',
      amount: '$450.00',
      receipt: true,
      icon: Hotel
    }]
  }, {
    id: 3,
    title: 'Tech Conference — Las Vegas',
    dates: 'Dec 02 - Dec 05, 2023',
    amount: '$2,100.00',
    status: 'Draft',
    items: 3,
    lineItems: [{
      id: 301,
      date: 'Dec 02',
      category: 'Flight',
      desc: 'DL 5678 JFK-LAS',
      amount: '$580.00',
      receipt: false,
      icon: Plane
    }]
  }, {
    id: 4,
    title: 'Partner Meeting — London',
    dates: 'Sep 15 - Sep 18, 2023',
    amount: '$3,450.00',
    status: 'Approved',
    items: 12,
    lineItems: []
  }, {
    id: 5,
    title: 'Sales Kickoff — Miami',
    dates: 'Aug 10 - Aug 12, 2023',
    amount: '$1,100.00',
    status: 'Rejected',
    items: 6,
    lineItems: []
  }];
  const filteredExpenses = activeTab === 'all' ? expenses : expenses.filter(e => e.status.toLowerCase() === activeTab.toLowerCase());
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-807">
            <CheckCircle className="w-3 h-3 mr-1" data-id="element-808" /> Approved
          </span>;
      case 'Submitted':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-809">
            <Clock className="w-3 h-3 mr-1" data-id="element-810" /> Submitted
          </span>;
      case 'Rejected':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-811">
            <XCircle className="w-3 h-3 mr-1" data-id="element-812" /> Rejected
          </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800" data-id="element-813">
            <FileText className="w-3 h-3 mr-1" data-id="element-814" /> Draft
          </span>;
    }
  };
  return <div className="space-y-6" data-id="element-815">
      <div className="flex items-center justify-between" data-id="element-816">
        <div data-id="element-817">
          <h2 className="text-2xl font-bold text-slate-800" data-id="element-818">
            Expense Management
          </h2>
          <p className="text-slate-500" data-id="element-819">
            Track reimbursements and submit reports.
          </p>
        </div>
        <button className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700" data-id="element-820">
          <Plus className="mr-2 h-4 w-4" data-id="element-821" />
          New Expense Report
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-3" data-id="element-822">
        {summaryStats.map(stat => <div key={stat.label} className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-823">
            <div className="flex items-center justify-between" data-id="element-824">
              <div data-id="element-825">
                <p className="text-sm font-medium text-slate-500" data-id="element-826">
                  {stat.label}
                </p>
                <h3 className="mt-2 text-2xl font-bold text-slate-900" data-id="element-827">
                  {stat.value}
                </h3>
              </div>
              <div className={`rounded-full p-3 ${stat.color}`} data-id="element-828">
                <stat.icon className="h-6 w-6" data-id="element-829" />
              </div>
            </div>
          </div>)}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200" data-id="element-830">
        <nav className="-mb-px flex space-x-8" data-id="element-831">
          {['All', 'Pending', 'Approved', 'Rejected'].map(tab => <button key={tab} onClick={() => setActiveTab(tab.toLowerCase() === 'pending' ? 'submitted' : tab.toLowerCase())} className={`
                whitespace-nowrap border-b-2 py-4 px-1 text-sm font-medium capitalize
                ${activeTab === tab.toLowerCase() || tab === 'Pending' && activeTab === 'submitted' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700'}
              `} data-id="element-832">
              {tab}
            </button>)}
        </nav>
      </div>

      {/* Expense Reports List */}
      <div className="space-y-4" data-id="element-833">
        {filteredExpenses.map(report => <div key={report.id} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden transition-all" data-id="element-834">
            <div className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-slate-50" onClick={() => toggleReport(report.id)} data-id="element-835">
              <div className="flex items-start gap-4" data-id="element-836">
                <div className="h-12 w-12 rounded-lg bg-blue-50 flex items-center justify-center text-blue-600 flex-shrink-0" data-id="element-837">
                  <Receipt className="h-6 w-6" data-id="element-838" />
                </div>
                <div data-id="element-839">
                  <h3 className="font-semibold text-slate-900" data-id="element-840">
                    {report.title}
                  </h3>
                  <div className="flex items-center gap-3 mt-1 text-sm text-slate-500" data-id="element-841">
                    <span className="flex items-center" data-id="element-842">
                      <Calendar className="h-3.5 w-3.5 mr-1" data-id="element-843" /> {report.dates}
                    </span>
                    <span data-id="element-844">•</span>
                    <span data-id="element-845">{report.items} items</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-6" data-id="element-846">
                <div className="text-right" data-id="element-847">
                  <p className="text-lg font-bold text-slate-900" data-id="element-848">
                    {report.amount}
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-849">Total Amount</p>
                </div>
                <div className="min-w-[100px] text-right" data-id="element-850">
                  {getStatusBadge(report.status)}
                </div>
                <div className="text-slate-400" data-id="element-851">
                  {expandedReportId === report.id ? <ChevronUp className="h-5 w-5" data-id="element-852" /> : <ChevronDown className="h-5 w-5" data-id="element-853" />}
                </div>
              </div>
            </div>

            {/* Expandable Details */}
            {expandedReportId === report.id && <div className="border-t border-slate-200 bg-slate-50 p-6 animate-in slide-in-from-top-2" data-id="element-854">
                <div className="mb-4 flex justify-between items-center" data-id="element-855">
                  <h4 className="font-medium text-slate-900" data-id="element-856">Line Items</h4>
                  <div className="flex gap-2" data-id="element-857">
                    {report.status === 'Draft' && <button className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700" data-id="element-858">
                        Submit Report
                      </button>}
                    <button className="px-3 py-1.5 border border-slate-300 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50" data-id="element-859">
                      Edit
                    </button>
                    <button className="px-3 py-1.5 border border-slate-300 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50" data-id="element-860">
                      View Receipt
                    </button>
                  </div>
                </div>

                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-id="element-861">
                  <table className="min-w-full divide-y divide-slate-200" data-id="element-862">
                    <thead className="bg-slate-50" data-id="element-863">
                      <tr data-id="element-864">
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-865">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-866">
                          Category
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-867">
                          Description
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase" data-id="element-868">
                          Receipt
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase" data-id="element-869">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200" data-id="element-870">
                      {report.lineItems.length > 0 ? report.lineItems.map(item => <tr key={item.id} data-id="element-871">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900" data-id="element-872">
                              {item.date}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500 flex items-center gap-2" data-id="element-873">
                              <item.icon className="h-4 w-4 text-slate-400" data-id="element-874" />
                              {item.category}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900" data-id="element-875">
                              {item.desc}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm" data-id="element-876">
                              {item.receipt ? <span className="text-emerald-600 flex items-center gap-1" data-id="element-877">
                                  <CheckCircle className="h-3 w-3" data-id="element-878" /> Attached
                                </span> : <span className="text-amber-600 flex items-center gap-1" data-id="element-879">
                                  <AlertCircle className="h-3 w-3" data-id="element-880" /> Missing
                                </span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right" data-id="element-881">
                              {item.amount}
                            </td>
                          </tr>) : <tr data-id="element-882">
                          <td colSpan={5} className="px-6 py-8 text-center text-sm text-slate-500" data-id="element-883">
                            No line items found. Add expenses to this report.
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>}
          </div>)}
      </div>
    </div>;
}